import { defineStore } from 'pinia';
import { client, getErrorMessage } from '@/api';
import { ElMessage } from 'element-plus';
import { CommentsFrequencySettingDto } from '@/api/models/comments-frequency-setting.dto';
import { useUserStore } from '@/store/user';

export type MinIntervalSecItem = {
  id: number;
  title: string;
};

export type FrequenciesState = {
  loading: boolean;
  error: string | null;

  settings: CommentsFrequencySettingDto | null;
  minIntervalsSec: MinIntervalSecItem[];
};

const getInitState = () => ({
  loading: false,
  error: null,
  settings: null,
  minIntervalsSec: [
    { id: 0, title: 'None' },
    { id: 1 * 60, title: '1 minute' },
    { id: 3 * 60, title: '3 minutes' },
    { id: 5 * 60, title: '5 minutes' },
    { id: 10 * 60, title: '10 minutes' },
    { id: 15 * 60, title: '15 minutes' },
  ],
});

export const useFrequencies = defineStore('frequencies', {
  state: (): FrequenciesState => getInitState(),

  actions: {
    reset() {
      this.$reset();
    },

    setLoading(value: boolean) { this.loading = value; },
    setError(value: string | null) { this.error = value; },

    setSettings(settings: CommentsFrequencySettingDto | null) {
      this.settings = (settings === null) ? null : { ...this.settings, ...settings };
    },

    async getPerGameUserMinCommentInterval(): Promise<CommentsFrequencySettingDto> {
      this.setError(null);
      this.setLoading(true);

      let settings: CommentsFrequencySettingDto = {
        minIntervalSec: 0,
        moderator: '',
      };

      try {
        settings = await client.frequenciesApi.getPerGameUserMinCommentInterval();

        this.setSettings(settings);
      } catch (err) {
        const message = getErrorMessage(err, 'Error during getting frequency settings');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return settings;
    },

    async updatePerGameUserMinCommentInterval(
      dto: CommentsFrequencySettingDto,
    ): Promise<Record<string, any>> {
      this.setError(null);
      this.setLoading(true);

      const userStore = useUserStore();

      try {
        await client.frequenciesApi.updatePerGameUserMinCommentInterval({
          ...dto,
          moderator: userStore.info.email as string,
        });

        this.setSettings(dto);

        ElMessage.success({
          message: 'Comment frequency was updated successfully',
          duration: 10000,
        });
      } catch (err) {
        ElMessage.error({
          message: getErrorMessage(err, 'Error during updating comment complaint'),
          duration: 10000,
        });
      } finally {
        this.setLoading(false);
      }

      return {};
    },
  },
});
